import type { PrintJobUpdatedEvent } from '../types/printing.types';
import { ticketPhrase } from './printing-format';

/* ==========================================================================
   Aviso de cupom sem confirmação

   Dispara no `printing.job_updated` com `overdue = true`: uma vez por
   trabalho em cada aba, ou uma vez por pedido quando o evento não traz
   trabalho (o despacho isolado, `dispatch_failed`). O registro do que já
   tocou vive no módulo, e não no componente, porque é por aba: navegar entre
   páginas não pode fazer o mesmo cupom tocar de novo.
   ========================================================================== */

const alreadyAlerted = new Set<string>();

/**
 * A chave do aviso, ou nulo quando o evento não pede aviso: sem atraso, sem
 * pedido (a página de teste), ou um cupom que já saiu da fila — o cancelado
 * pela pausa ou pela desativação chega com `overdue = false` e não toca.
 */
export function overdueAlertKey(event: PrintJobUpdatedEvent): string | null {
  if (!event.overdue || !event.orderId) return null;
  if (event.jobId) {
    if (event.status !== 'pending' && event.status !== 'delivered') return null;
    return `job:${event.jobId}`;
  }
  if (event.errorCode !== 'dispatch_failed') return null;
  return `order:${event.orderId}`;
}

/** Marca o aviso como dado nesta aba; falso quando ele já tinha tocado. */
export function claimOverdueAlert(key: string): boolean {
  if (alreadyAlerted.has(key)) return false;
  alreadyAlerted.add(key);
  return true;
}

/** Só para teste: esquece os avisos dados nesta aba. */
export function resetOverdueAlerts(): void {
  alreadyAlerted.clear();
}

/** Prazo do atraso quando o evento não o traz: o padrão da API. */
const DEFAULT_OVERDUE_SECONDS = 60;

/**
 * "Pedido 23: a comanda da Chapa não confirmou em 60 s", ou "Pedido 23: os
 * cupons não foram gerados" no despacho isolado. Número, ponto e prazo vêm do
 * próprio evento, sem leitura extra; sem número do dia, só "Pedido".
 */
export function describeOverdue(event: PrintJobUpdatedEvent): string {
  const order = event.dailyNumber != null ? `Pedido ${event.dailyNumber}` : 'Pedido';
  if (!event.jobId) return `${order}: os cupons não foram gerados`;
  const seconds = event.overdueAfterSeconds ?? DEFAULT_OVERDUE_SECONDS;
  return `${order}: ${ticketPhrase(event.kind, event.stationName)} não confirmou em ${seconds} s`;
}
